// The draft room, without a host around it (BACKLOG D10, REALTIME.md,
// PLAN-2026-10-14 §1, MOCK_DRAFT.md §5).
//
// PURE, SO IT CAN BE TESTED WITHOUT A RUNTIME AND RUN IN TWO HOSTS. Everything
// the room decides — who may do what, what the engine is asked, what each
// socket is sent — is here. The live worker's Durable Object
// (`workers/live/src/draft-room.ts`) only stores, wakes and sends for a real
// draft; the browser hosts it for a mock, on a clock of its own. A test hands
// this a prepared league and a clock and reads what comes back.
//
// THE RING HOLDS FACTS, NOT PAYLOADS. Each event the room records is a type
// and the pick or seat it is about. What goes on the wire is rendered from the
// room's state for the socket it goes to — its own team, its own queue — at
// the moment it is sent. Rendered live, that is the event's own value; rendered
// on a resync, later events have already moved the state on, so each replayed
// event carries the newer value and the client ends where the room is. That is
// REALTIME's promise ("either way the client ends consistent"), kept without
// storing a copy of every viewer's half of every event.

import type {
  DraftAuction,
  DraftBoardCell,
  DraftMine,
  DraftOfferClosed,
  DraftPlayerRow,
  DraftSeat,
  DraftServerMessage,
  DraftTradeItem,
  DraftTradeOffer,
  DraftTrading,
  DraftView,
  ErrorCode,
  PlayerSummary,
} from '@fsp/domain';
import {
  advance,
  apply,
  auctionValues,
  createDraft,
  currentOverall,
  keeperAt,
  carriedBy,
  currentTeam,
  holderOf,
  isTaken,
  moneyLeft,
  mostFor,
  nextAt,
  overallOf,
  ownerOf,
  replayTrades,
  rosterOf,
  spentBy,
  spotsLeft,
  teamForPick,
  totalPicks,
  tradeProblem,
  type AiManager,
  type DraftContext,
  type DraftEvent,
  type DraftFacts,
  type DraftIntent,
  type DraftOffer,
  type DraftState,
  type DraftTrade,
  type TradeItem,
  wantedTaken,
} from '@fsp/draft';
import { contextFromParts, draftStyleOf, styleRecap, styleWords } from '@fsp/draft';
import type { DraftClientMessage } from '@fsp/domain';
import type { LiveDraftPrepared, LivePlayer } from './prepared';

/** Who is on the other end of a socket, as the Pages Function resolved them at connect. */
export interface Viewer {
  userId: string;
  name: string;
  /** Their team in this draft, or null for a member without a seat. */
  teamId: string | null;
  isCommissioner: boolean;
}

/** One recorded event: what happened, and to whom it is about. */
/** What an empty seat in a league mock's lobby is called, until somebody takes it. */
export const OPEN_SEAT = 'Open seat';

/** When a clock reaches zero, and how much of it is left, past the hours a league stops it. */
export type RoomClock = Required<Pick<DraftContext, 'clockEnds' | 'clockLeft'>>;

export interface RingEvent {
  seq: number;
  at: string;
  type:
    | 'draft.started'
    | 'draft.picked'
    | 'draft.turn'
    | 'draft.paused'
    | 'draft.resumed'
    | 'draft.undone'
    | 'draft.clock'
    | 'draft.seat'
    | 'draft.queue'
    | 'draft.completed'
    | 'draft.traded'
    | 'draft.trade.undone'
    | 'draft.offers'
    /** An auction's: the block changed, or a player sold (AUCTION.md §11). */
    | 'draft.block'
    | 'draft.sold';
  overall?: number;
  playerId?: string;
  teamId?: string;
  /** Sent to its own team alone: not numbered in the sequence and not kept in the ring. */
  own?: true;
  /** A trade, or one undone: the board cells it moved, and the trade itself for the room's line. */
  overalls?: number[];
  trade?: DraftTrade;
  /** A team's offers that stopped waiting, and why. */
  closed?: DraftOfferClosed[];
}

/** What a step of the room produced. */
export interface RoomStep {
  /** True when the room's state changed and must be stored. */
  changed: boolean;
  /** For every socket (a `draft.queue` only for its own team's), in order. */
  events: RingEvent[];
  /** For the socket that asked, alone. */
  reply: DraftServerMessage | null;
  /**
   * A league mock's seats changed hands in its lobby (MOCK_DRAFT.md §6.2):
   * the host stores the prepared draft and sends everybody the room again,
   * since whose seat is whose is in every view.
   */
  seatsChanged?: boolean;
  /** A commissioner asked to start: the host seats the bots and calls `start`. */
  startAsked?: boolean;
  /** A client asked to catch up from this `seq`. */
  resyncFrom?: number;
}

const iso = (ms: number): string => new Date(ms).toISOString();

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th, 21st. */
function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${String(n)}${suffix}`;
}

/**
 * The engine's refusals, widened to the one vocabulary on the wire (REALTIME
 * "what the engine refuses"): `details.reason` is the engine's own word.
 */
const WIDENED: Record<Extract<DraftEvent, { type: 'rejected' }>['code'], ErrorCode> = {
  'not-live': 'conflict',
  'not-your-turn': 'conflict',
  taken: 'conflict',
  'nothing-to-undo': 'conflict',
  'already-started': 'conflict',
  'unknown-player': 'invalid',
  'unknown-team': 'invalid',
  'bad-trade': 'invalid',
  'stale-trade': 'conflict',
  // AN AUCTION'S: each is the room having moved on, or the team's own money.
  'too-much': 'conflict',
  'too-little': 'conflict',
  'no-room': 'conflict',
  'lot-closed': 'conflict',
  ahead: 'conflict',
};

/** The bots in a prepared draft's seats, as the engine takes them. */
export function botsOf(prepared: LiveDraftPrepared): Record<string, AiManager> {
  return Object.fromEntries(
    prepared.seats.flatMap((s) => (s.bot ? [[s.teamId, s.bot.manager] as const] : [])),
  );
}

/** A new room's state, from a prepared draft: the lobby, in its seat order. */
export function lobbyState(prepared: LiveDraftPrepared): DraftState {
  return createDraft({
    id: prepared.draftId,
    type: prepared.type,
    order: prepared.order,
    rounds: prepared.rounds,
    pickSeconds: prepared.pickSeconds,
    ai: botsOf(prepared),
    whenTheClockExpires: prepared.whenTheClockExpires,
    autopilotOnExpiry: true,
    // TRADING IS THE LEAGUE'S DRAFT'S, never a mock's: a mock is practice
    // against the board (DRAFT_ROOM.md "Trading during the draft").
    trading: prepared.trading === true && prepared.mock === undefined,
    ...(prepared.auction ? { auction: prepared.auction } : {}),
    // KEEPERS ARE THE LEAGUE'S DRAFT'S, never a mock's (§2.3).
    ...(prepared.keepers && prepared.mock === undefined ? { keepers: prepared.keepers } : {}),
    // A DYNASTY'S CARRIED ROSTERS ARE BOTH'S (§2.4 rule 4): a mock of a rookie
    // draft is practice for teams that hold their players.
    ...(prepared.held ? { held: prepared.held } : {}),
    // THE PICKS TRADED BEFORE THE DRAFT are the league's draft's (§2.6 rule
    // 5); a mock drafts every seat's own.
    ...(prepared.owners && prepared.mock === undefined ? { owners: prepared.owners } : {}),
  });
}

/**
 * An auction's values in its money (AUCTION.md §7), from what each player is
 * worth over a replacement; null for a pick draft.
 */
export function auctionValuesOf(prepared: LiveDraftPrepared): Record<string, number> | null {
  if (!prepared.auction) return null;
  return auctionValues(prepared.players, {
    teams: prepared.order.length,
    rounds: prepared.rounds,
    budget: prepared.auction.budget,
    minBid: prepared.auction.minBid,
  });
}

/** Every player the room may show: the board's, and a dynasty's carried ones on their teams. */
function playersById(prepared: LiveDraftPrepared): Map<string, LivePlayer> {
  return new Map([...prepared.players, ...(prepared.carried ?? [])].map((p) => [p.id, p]));
}

/**
 * The engine's context for a prepared room: the league's, each player's facts
 * and worth for the bots' styles (AI_MANAGERS.md §4.7), and an auction's values.
 */
function contextOf(
  prepared: LiveDraftPrepared,
  values: Record<string, number> | null,
): DraftContext {
  const facts: Record<string, DraftFacts> = {};
  const worth: Record<string, number> = {};
  for (const p of prepared.players) {
    if (p.facts) facts[p.id] = p.facts;
    worth[p.id] = p.value;
  }
  const ctx = {
    ...contextFromParts(prepared.context, prepared.context.sport === undefined ? undefined : facts),
    values: worth,
  };
  return values === null ? ctx : { ...ctx, auctionValues: values };
}

export class Room {
  prepared: LiveDraftPrepared;
  state: DraftState;
  seq: number;
  private ctx: DraftContext;
  private byId: Map<string, LivePlayer>;
  /** An auction's values in its money; null for a pick draft. */
  private values: Record<string, number> | null;
  /** The clock the league's rules gave the lobby, to tell a commissioner's edit from it. */
  private lobbyClock: number;
  /**
   * When a clock reaches zero past the hours the league stops it (C7,
   * B-341). The host's, because the arithmetic is wall-clock in a zone; the
   * room in the browser, a mock, has none.
   */
  private clock: RoomClock | null = null;

  /**
   * The intents already acted on, newest last, so a client that retries one
   * after a drop (REALTIME.md: "a retry after a dropped connection is
   * idempotent") does not undo two picks or restart a clock twice. The host
   * stores it with the state.
   */
  seen: string[];

  constructor(prepared: LiveDraftPrepared, state: DraftState, seq: number, seen: string[] = []) {
    this.prepared = prepared;
    this.state = state;
    this.seq = seq;
    this.seen = seen;
    this.values = auctionValuesOf(prepared);
    this.ctx = contextOf(prepared, this.values);
    this.byId = playersById(prepared);
    this.lobbyClock = prepared.pickSeconds;
  }

  /**
   * A fresh read of the league, before the draft starts: the seats, the
   * board and everybody's priorities. Queues are kept; the seat order is the
   * new one, because nobody has picked in the old. What it returns is for the
   * host to send: offers the new read ended, to their two teams.
   */
  reprepare(prepared: LiveDraftPrepared, now: number): RingEvent[] {
    this.prepared = prepared;
    this.values = auctionValuesOf(prepared);
    this.ctx = contextOf(prepared, this.values);
    this.byId = playersById(prepared);
    if (this.state.status === 'lobby') {
      // WHAT PEOPLE SET IN THE LOBBY IS KEPT: their queues, who turned auto
      // on, and a clock the commissioner changed. Only what the league's rules
      // say is read again.
      const before = this.state;
      const edited = before.pickSeconds !== this.lobbyClock;
      this.state = lobbyState(prepared);
      for (const teamId of prepared.order) {
        const kept = before.queues[teamId];
        if (kept) this.state.queues[teamId] = kept.filter((id) => this.byId.has(id));
        if (before.autopilot[teamId] && !this.state.ai[teamId]) this.state.autopilot[teamId] = true;
      }
      if (edited) this.state.pickSeconds = before.pickSeconds;
      // AND THE TRADES MADE IN THE LOBBY, REPLAYED over the new read, and the
      // offers waiting: a pick is named by its round and its team's slot, so
      // it means the same pick whatever the order becomes. Replayed rather
      // than kept as they were, so a trade that no longer holds — a seat
      // closed under it — takes whatever leaned on it with it, and no pick is
      // left with a team no kept trade gave it to (the review of B-345).
      if (before.trades?.length || before.offers?.length) {
        this.state = replayTrades(
          { ...this.state, offers: before.offers ?? [] },
          before.trades ?? [],
        );
      }
      // THE TRADES' COUNT IS KEPT EVEN WITH NONE LEFT: a trade undone in the
      // lobby keeps its number, and the next one must not take it — the
      // room's line in chat is keyed on it, and a second line under the same
      // key is never posted.
      if (before.tradeSeq) this.state = { ...this.state, tradeSeq: before.tradeSeq };
      this.lobbyClock = prepared.pickSeconds;
    } else {
      this.state = { ...this.state, ai: botsOf(prepared) };
    }
    return this.settleOffers(now);
  }

  /**
   * Offers nobody can answer now, ended, and their two teams told: one with a
   * seat no person holds any more — its manager left, a bot took it at the
   * start — or one the new read made untrue.
   */
  private settleOffers(now: number): RingEvent[] {
    const offers = this.state.offers ?? [];
    const person = (teamId: string) => {
      const seat = this.prepared.seats.find((s) => s.teamId === teamId);
      return seat !== undefined && seat.ownerUserId !== null && seat.bot === null;
    };
    const dead = offers.filter(
      (o) => !person(o.fromTeamId) || !person(o.toTeamId) || tradeProblem(this.state, o) !== null,
    );
    if (dead.length === 0) return [];
    this.state = { ...this.state, offers: offers.filter((o) => !dead.includes(o)) };
    return this.record(
      [
        {
          type: 'offers.changed',
          teamIds: [...new Set(dead.flatMap((o) => [o.fromTeamId, o.toTeamId]))],
          closed: dead.map((offer) => ({ offer, reason: 'void' as const })),
        },
      ],
      now,
    );
  }

  // ── Reading ─────────────────────────────────────────────────────────────

  private summary(id: string): PlayerSummary | null {
    return this.byId.get(id)?.summary ?? null;
  }

  private live(): boolean {
    return this.state.status === 'live' || this.state.status === 'paused';
  }

  current(viewer: Viewer | null): DraftView['current'] {
    if (!this.live()) return null;
    const teamId = currentTeam(this.state);
    if (teamId === null) return null;
    const overall = currentOverall(this.state);
    return {
      overall,
      round: Math.ceil(overall / this.state.order.length),
      teamId,
      isMine: viewer?.teamId === teamId,
      deadlineAt: this.state.deadlineAt === null ? null : iso(this.state.deadlineAt),
      sniped: this.snipedNow(teamId),
    };
  }

  /** Whether the bot on the clock wanted the player just taken (B-529), once a pick. */
  private sniped: { picks: number; teamId: string; value: boolean } | null = null;
  private snipedNow(teamId: string): boolean {
    const picks = this.state.picks.length;
    const kept = this.sniped;
    if (kept !== null && kept.picks === picks && kept.teamId === teamId) return kept.value;
    const value = this.state.auction ? false : wantedTaken(this.state, teamId, this.ctx);
    this.sniped = { picks, teamId, value };
    return value;
  }

  upcoming(): DraftView['upcoming'] {
    // AN AUCTION HAS NO ORDER OF PICKS to come, only nominations.
    if (!this.live() || this.state.auction) return [];
    const now = currentOverall(this.state);
    const total = totalPicks(this.state);
    return [1, 2, 3]
      .map((k) => now + k)
      .filter((o) => o <= total)
      .map((o) => ({ overall: o, teamId: ownerOf(this.state, o) }));
  }

  cell(overall: number): DraftBoardCell {
    const n = this.state.order.length;
    const pick = this.state.picks.find((p) => p.overall === overall) ?? null;
    // A KEEPER ON A PICK TO COME is on the board already (§2.3 rule 7).
    const kept = pick === null ? keeperAt(this.state, overall) : null;
    // WHOSE PICK IT IS NOW, OR WHO MADE IT; the slot's own team beside it
    // when a trade moved the pick, and who has the player when one moved him.
    const teamId = pick?.teamId ?? kept?.teamId ?? ownerOf(this.state, overall);
    const original = teamForPick(this.state, overall);
    const holder = pick === null ? null : holderOf(this.state, pick);
    return {
      overall,
      round: Math.ceil(overall / n),
      slot: ((overall - 1) % n) + 1,
      teamId,
      player: pick ? this.summary(pick.playerId) : kept ? this.summary(kept.playerId) : null,
      by: pick?.by ?? (kept ? 'keeper' : null),
      isCurrent: this.live() && !this.state.auction && currentOverall(this.state) === overall,
      originalTeamId: original === teamId ? null : original,
      heldBy: holder === null || holder === teamId ? null : holder,
      price: pick?.price ?? null,
    };
  }

  lastPick(): DraftBoardCell | null {
    const last = this.state.picks[this.state.picks.length - 1];
    return last ? this.cell(last.overall) : null;
  }

  seat(teamId: string, connected: ReadonlySet<string>): DraftSeat {
    const seat = this.prepared.seats.find((s) => s.teamId === teamId)!;
    return {
      team: seat.team,
      slot: this.state.order.indexOf(teamId) + 1,
      ai: seat.bot
        ? {
            name: seat.bot.manager.name,
            voice: seat.bot.manager.voice,
            difficulty: seat.bot.difficulty,
            style: this.styleOf(seat.bot.manager),
          }
        : null,
      // A bot is always in the room.
      connected: seat.bot !== null || connected.has(teamId),
      autopilot: Boolean(this.state.autopilot[teamId]),
    };
  }

  /** A bot's draft style in a few phrases, for its seat (AI_MANAGERS.md §4.7); none without one. */
  private styleOf(manager: AiManager): string[] {
    const style = draftStyleOf(manager, this.ctx, this.state.id);
    return style === null ? [] : styleWords(style, this.ctx, this.state.auction !== undefined);
  }

  /**
   * What a bot seat's style came to in these players, for the recap ("took 4
   * Vikings, and no Packers", `styleRecap`); null for a person's seat.
   */
  styleRecapOf(teamId: string, playerIds: readonly string[]): string | null {
    const manager = this.state.ai[teamId];
    if (!manager) return null;
    const style = draftStyleOf(manager, this.ctx, this.state.id);
    return style === null ? null : styleRecap(style, playerIds, this.ctx);
  }

  /** The positions this team still has a starting slot open at. */
  needs(teamId: string): string[] {
    const have = new Map<string, number>();
    // THE PLAYERS IT HAS NOW, a trade's included (`rosterOf`), and those it
    // carried into a dynasty's draft (`carriedBy`).
    for (const id of [...carriedBy(this.state, teamId), ...rosterOf(this.state, teamId)]) {
      const pos = this.byId.get(id)?.position ?? '';
      have.set(pos, (have.get(pos) ?? 0) + 1);
    }
    const want = new Map<string, number>();
    for (const { position, count } of this.prepared.needs) {
      want.set(position, (want.get(position) ?? 0) + count);
    }
    return [...want].filter(([pos, count]) => (have.get(pos) ?? 0) < count).map(([pos]) => pos);
  }

  /**
   * What the draft put in each of a team's slots: the dedicated slots first,
   * then the ones that take several positions, then the bench, answered in the
   * league's own order (the REST read's rule, `myRosterOf`). Reserve and taxi
   * slots are not filled by a draft.
   */
  roster(teamId: string): DraftView['myRoster'] {
    const left = [...carriedBy(this.state, teamId), ...rosterOf(this.state, teamId)];
    const order = this.prepared.slots
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.kind === 'starter' || s.kind === 'bench')
      .sort(
        (a, b) =>
          (a.s.kind === 'bench' ? 2 : a.s.eligible.length === 1 ? 0 : 1) -
            (b.s.kind === 'bench' ? 2 : b.s.eligible.length === 1 ? 0 : 1) || a.i - b.i,
      );
    const out: { at: number; k: number; row: DraftView['myRoster'][number] }[] = [];
    for (const { s, i } of order) {
      for (let k = 0; k < s.count; k += 1) {
        const takes = left.find((id) => {
          if (s.eligible.length === 0) return true;
          const positions = this.byId.get(id)?.summary.positions ?? [];
          return positions.some((p) => s.eligible.includes(p));
        });
        if (takes !== undefined) left.splice(left.indexOf(takes), 1);
        out.push({
          at: i,
          k,
          row: {
            slot: s.slot,
            slotName: s.slotName,
            player: takes === undefined ? null : this.summary(takes),
          },
        });
      }
    }
    // EVERYBODY ELSE ON THE BENCH, past its size: a dynasty's roster carried
    // whole fills every cell before the draft, and each rookie it takes is
    // still the team's (PLAN-2026-10-27-the-next-season §2.4 rule 5). The
    // draft's own picks first, then the rest it carried.
    const bench = this.prepared.slots.find((s) => s.kind === 'bench');
    const picks = new Set(rosterOf(this.state, teamId));
    const extra = [...left]
      .sort((a, b) => Number(picks.has(b)) - Number(picks.has(a)))
      .map((id) => ({
        slot: bench?.slot ?? 'BN',
        slotName: bench?.slotName ?? 'Bench',
        player: this.summary(id),
      }));
    return [...out.sort((a, b) => a.at - b.at || a.k - b.k).map((o) => o.row), ...extra];
  }

  mine(viewer: Viewer | null): DraftMine {
    const teamId = viewer?.teamId ?? null;
    if (teamId === null) {
      return { myRoster: [], myQueue: [], myNeeds: [], queuedIds: [], fillsNeedIds: [] };
    }
    const queue = (this.state.queues[teamId] ?? []).filter((id) => !isTaken(this.state, id));
    const needs = this.needs(teamId);
    return {
      myRoster: this.roster(teamId),
      myQueue: queue.flatMap((id) => this.summary(id) ?? []),
      myNeeds: needs,
      queuedIds: queue,
      fillsNeedIds: this.prepared.players
        .filter((p) => needs.includes(p.position) && !isTaken(this.state, p.id))
        .map((p) => p.id),
    };
  }

  row(id: string, mine: DraftMine | null): DraftPlayerRow | null {
    const p = this.byId.get(id);
    if (!p) return null;
    return {
      player: p.summary,
      rank: p.rank,
      projection: null,
      adp: null,
      tier: p.tier,
      positionRank: p.positionRank,
      bye: p.bye,
      last: p.last,
      queued: mine?.queuedIds.includes(id) ?? false,
      fillsNeed: mine?.fillsNeedIds.includes(id) ?? false,
      value: this.values?.[id] ?? null,
    };
  }

  /**
   * An auction's half of the room for one reader (AUCTION.md §10): the
   * players up, soonest to sell first, with the reader's own auto-bid and most
   * on each; whose nomination it is; and every team's money. Null for a pick
   * draft.
   */
  auction(viewer: Viewer | null): DraftAuction | null {
    const a = this.state.auction;
    if (!a) return null;
    const me = viewer?.teamId ?? null;
    const lots = [...a.lots]
      .sort(
        (x, y) =>
          (x.deadlineAt ?? Number.MAX_SAFE_INTEGER) - (y.deadlineAt ?? Number.MAX_SAFE_INTEGER) ||
          x.id - y.id,
      )
      .flatMap((lot) => {
        const player = this.summary(lot.playerId);
        if (player === null) return [];
        const mine = me === null ? undefined : lot.maxes[me];
        return [
          {
            id: lot.id,
            player,
            nominatedBy: lot.nominatedBy,
            price: lot.price,
            leaderTeamId: lot.leader,
            deadlineAt: lot.deadlineAt === null ? null : iso(lot.deadlineAt),
            myAuto: mine?.auto ? mine.amount : null,
            myMost: me === null ? 0 : mostFor(this.state, me, lot.id),
            value: this.values?.[lot.playerId] ?? null,
            bids: lot.bids.slice(-10).map((b) => ({
              teamId: b.teamId,
              amount: b.amount,
              at: iso(b.at),
            })),
          },
        ];
      });
    return {
      rules: {
        budget: a.rules.budget,
        minBid: a.rules.minBid,
        nominationSeconds: a.rules.nominationSeconds,
        bidSeconds: a.rules.bidSeconds,
        resetSeconds: a.rules.resetSeconds,
        lotsAtOnce: a.rules.lotsAtOnce,
      },
      lots,
      nominating:
        a.nominator === null || !this.live()
          ? null
          : {
              teamId: a.nominator,
              isMine: me === a.nominator,
              deadlineAt: a.nominateBy === null ? null : iso(a.nominateBy),
            },
      budgets: this.state.order.map((teamId) => ({
        teamId,
        spent: spentBy(this.state, teamId),
        left: moneyLeft(this.state, teamId),
        most: mostFor(this.state, teamId, null),
        spotsLeft: spotsLeft(this.state, teamId),
      })),
    };
  }

  /** Whether this draft trades: the league's, never a mock's. Whether it is over is `status`'s to say. */
  private tradingAllowed(): boolean {
    return this.state.trading === true;
  }

  /** One thing in a trade, as a screen names it; null for a player the board no longer has. */
  private tradeItem(item: TradeItem): DraftTradeItem | null {
    const n = this.state.order.length;
    if (item.kind === 'pick') {
      const overall = overallOf(this.state, item.round, item.teamId);
      if (overall === null) return null;
      return {
        kind: 'pick',
        round: item.round,
        teamId: item.teamId,
        overall,
        slot: ((overall - 1) % n) + 1,
      };
    }
    const pick = this.state.picks.find((p) => p.playerId === item.playerId);
    const player = this.summary(item.playerId);
    if (pick === undefined || player === null) return null;
    return { kind: 'player', player, overall: pick.overall };
  }

  private tradeOffer(o: DraftOffer): DraftTradeOffer {
    const items = (list: readonly TradeItem[]) =>
      list.flatMap((i) => {
        const shown = this.tradeItem(i);
        return shown === null ? [] : [shown];
      });
    return {
      id: o.id,
      fromTeamId: o.fromTeamId,
      toTeamId: o.toTeamId,
      give: items(o.give),
      get: items(o.get),
      at: iso(o.at),
    };
  }

  /**
   * The trading half of the room for one reader: whether it takes offers, the
   * offers to and from their seat alone, and every trade made.
   */
  trading(viewer: Viewer | null): DraftTrading {
    const teamId = viewer?.teamId ?? null;
    return {
      allowed: this.tradingAllowed(),
      offers:
        teamId === null
          ? []
          : (this.state.offers ?? [])
              .filter((o) => o.fromTeamId === teamId || o.toTeamId === teamId)
              .map((o) => this.tradeOffer(o)),
      trades: (this.state.trades ?? []).map((t) => ({
        ...this.tradeOffer(t),
        seq: t.seq,
        acceptedAt: iso(t.acceptedAt),
      })),
    };
  }

  /** A seat's manager as the room names them: the person, the bot, or the seat. */
  private nameOf(teamId: string): string {
    return this.prepared.seats.find((s) => s.teamId === teamId)?.team.ownerName ?? 'A team';
  }

  /**
   * Things in a trade, in words: *"pick 4.04 and Saquon Barkley"*. Before the
   * order is set, a pick's number can still move, so it is named by whose
   * slot it is and its round: *"Casey's 4th-round pick"*.
   */
  itemsLine(items: readonly TradeItem[]): string {
    const n = this.state.order.length;
    const words = items.map((item) => {
      if (item.kind === 'player') return this.summary(item.playerId)?.name ?? 'a player';
      if (this.state.status === 'lobby' && !this.prepared.orderFixed) {
        return `${this.nameOf(item.teamId)}’s ${ordinal(item.round)}-round pick`;
      }
      const overall = overallOf(this.state, item.round, item.teamId) ?? 0;
      return `pick ${String(item.round)}.${String(((overall - 1) % n) + 1).padStart(2, '0')}`;
    });
    if (words.length <= 1) return words.join('');
    return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]!}`;
  }

  /**
   * A trade, or one undone, as the room says it in the league's chat: a trade
   * is news to the league, where a pick is not (B-328).
   */
  tradeLine(trade: DraftTrade, undone: boolean): string {
    const from = this.nameOf(trade.fromTeamId);
    const to = this.nameOf(trade.toTeamId);
    if (undone) return `The commissioner undid the trade between ${from} and ${to}.`;
    return `${from} and ${to} traded. ${from} gets ${this.itemsLine(trade.get)}; ${to} gets ${this.itemsLine(trade.give)}.`;
  }

  /** The board cells a trade moved: its picks, and the picks its players were drafted with. */
  private tradeCells(trade: DraftTrade): number[] {
    return [...trade.give, ...trade.get].flatMap((item) => {
      if (item.kind === 'pick') {
        const overall = overallOf(this.state, item.round, item.teamId);
        return overall === null ? [] : [overall];
      }
      const pick = this.state.picks.find((p) => p.playerId === item.playerId);
      return pick === undefined ? [] : [pick.overall];
    });
  }

  view(viewer: Viewer | null, connected: ReadonlySet<string>, now: number): DraftView {
    const mine = this.mine(viewer);
    const queued = new Set(mine.queuedIds);
    const fills = new Set(mine.fillsNeedIds);
    const n = this.state.order.length;
    const total = n * this.state.rounds;
    return {
      league: {
        ...this.prepared.league,
        myTeamId: viewer?.teamId ?? null,
        isCommissioner: viewer?.isCommissioner ?? false,
      },
      status: this.state.status,
      type: this.state.auction ? 'auction' : this.state.type,
      mode: this.prepared.mode,
      rounds: this.state.rounds,
      pickSeconds: this.state.pickSeconds,
      whenTheClockExpires: this.state.whenTheClockExpires,
      basis: this.prepared.basis,
      // A room stored before this field had none; its players' rows still say.
      byeWeeks: this.prepared.byeWeeks ?? {},
      scheduledAt: this.prepared.scheduledAt,
      // Somebody reading this is in the room, so it is open.
      roomOpen: true,
      seats: this.state.order.map((teamId) => {
        const seat = this.seat(teamId, connected);
        return { ...seat, team: { ...seat.team, isMine: teamId === viewer?.teamId } };
      }),
      current: this.current(viewer),
      upcoming: this.upcoming(),
      board: Array.from({ length: total }, (_, i) => this.cell(i + 1)),
      available: this.prepared.players
        .filter((p) => !isTaken(this.state, p.id))
        .map((p) => ({
          ...this.row(p.id, null)!,
          queued: queued.has(p.id),
          fillsNeed: fills.has(p.id),
        })),
      myTeamId: viewer?.teamId ?? null,
      myQueue: mine.myQueue,
      myRoster: mine.myRoster,
      myNeeds: mine.myNeeds,
      // The room's talk is the league's channel, read where chat is read.
      chat: [],
      isCommissioner: viewer?.isCommissioner ?? false,
      lastPick: this.lastPick(),
      mockRoom:
        this.prepared.mock === undefined
          ? null
          : {
              mockId: this.prepared.mock.mockId,
              host: this.prepared.mock.hostName,
              isHost: viewer?.userId === this.prepared.mock.hostUserId,
              closesAt: this.state.status === 'lobby' ? this.prepared.mock.closesAt : null,
            },
      overnightPause:
        this.prepared.overnightPause && this.prepared.timeZone
          ? { ...this.prepared.overnightPause, zone: this.prepared.timeZone }
          : null,
      trading: this.trading(viewer),
      auction: this.auction(viewer),
      updatedAt: iso(now),
    };
  }

  snapshot(viewer: Viewer | null, connected: ReadonlySet<string>, now: number): DraftServerMessage {
    return {
      type: 'snapshot',
      seq: this.seq,
      at: iso(now),
      payload: { view: this.view(viewer, connected, now) },
    };
  }

  /**
   * A recorded event, for one socket. Null when it is not that socket's to
   * see: another team's queue.
   */
  render(
    ev: RingEvent,
    viewer: Viewer | null,
    connected: ReadonlySet<string>,
  ): DraftServerMessage | null {
    const base = { seq: ev.seq, at: ev.at };
    const status = this.state.status;
    switch (ev.type) {
      case 'draft.started':
        return { ...base, type: ev.type, payload: { status, current: this.current(viewer) } };
      case 'draft.turn':
        return {
          ...base,
          type: ev.type,
          payload: { current: this.current(viewer), upcoming: this.upcoming() },
        };
      case 'draft.paused':
      case 'draft.resumed':
        return { ...base, type: ev.type, payload: { status, current: this.current(viewer) } };
      case 'draft.picked':
        return {
          ...base,
          type: ev.type,
          payload: {
            cell: this.cell(ev.overall!),
            removedPlayerId: ev.playerId!,
            current: this.current(viewer),
            upcoming: this.upcoming(),
            lastPick: this.lastPick(),
            mine: this.mine(viewer),
          },
        };
      case 'draft.undone': {
        const mine = this.mine(viewer);
        const restored = this.row(ev.playerId!, mine);
        if (restored === null) return null;
        return {
          ...base,
          type: ev.type,
          payload: {
            cell: this.cell(ev.overall!),
            restoredPlayer: restored,
            current: this.current(viewer),
            upcoming: this.upcoming(),
            lastPick: this.lastPick(),
            mine,
          },
        };
      }
      case 'draft.clock':
        return {
          ...base,
          type: ev.type,
          payload: { pickSeconds: this.state.pickSeconds, current: this.current(viewer) },
        };
      case 'draft.seat':
        return {
          ...base,
          type: ev.type,
          payload: {
            seat: (() => {
              const seat = this.seat(ev.teamId!, connected);
              return { ...seat, team: { ...seat.team, isMine: ev.teamId === viewer?.teamId } };
            })(),
          },
        };
      case 'draft.queue':
        if (ev.teamId !== viewer?.teamId) return null;
        return { ...base, type: ev.type, payload: { mine: this.mine(viewer) } };
      case 'draft.completed':
        return { ...base, type: ev.type, payload: { status, current: null } };
      case 'draft.traded':
      case 'draft.trade.undone':
        return {
          ...base,
          type: ev.type,
          payload: {
            cells: (ev.overalls ?? []).map((o) => this.cell(o)),
            current: this.current(viewer),
            upcoming: this.upcoming(),
            // The last pick's player may be the one traded: who has him is on it.
            lastPick: this.lastPick(),
            mine: this.mine(viewer),
            trading: this.trading(viewer),
          },
        };
      case 'draft.offers':
        if (ev.teamId !== viewer?.teamId) return null;
        return {
          ...base,
          type: ev.type,
          payload: { trading: this.trading(viewer), closed: ev.closed ?? [] },
        };
      case 'draft.block': {
        // A TEAM'S OWN AUTO-BID that moved nothing is that team's alone.
        if (ev.own && ev.teamId !== viewer?.teamId) return null;
        const auction = this.auction(viewer);
        if (auction === null) return null;
        return { ...base, type: ev.type, payload: { auction } };
      }
      case 'draft.sold': {
        const auction = this.auction(viewer);
        if (auction === null) return null;
        return {
          ...base,
          type: ev.type,
          payload: {
            cell: this.cell(ev.overall!),
            removedPlayerId: ev.playerId!,
            lastPick: this.lastPick(),
            mine: this.mine(viewer),
            auction,
          },
        };
      }
    }
  }

  // ── Acting ──────────────────────────────────────────────────────────────

  private record(events: readonly DraftEvent[], now: number): RingEvent[] {
    const out: RingEvent[] = [];
    const moved = events.some(
      (e) =>
        e.type === 'pick.made' ||
        e.type === 'undone' ||
        e.type === 'resumed' ||
        e.type === 'started',
    );
    const push = (type: RingEvent['type'], at: number, extra: Partial<RingEvent> = {}) => {
      this.seq += 1;
      out.push({ seq: this.seq, at: iso(at), type, ...extra });
    };
    // AN AUCTION'S BLOCK IS SENT ONCE A STEP, after everything in it: a bid a
    // bot made and the auto-bid that answered it are one change to a reader,
    // and each message is rendered from the room as it ends up anyway.
    const auction = this.state.auction !== undefined;
    let block = false;
    const ownBlocks = new Set<string>();
    for (const e of events) {
      switch (e.type) {
        case 'started':
          push('draft.started', e.at);
          break;
        case 'nominated':
        case 'bid':
        case 'nominator':
          block = true;
          break;
        case 'sold':
          push('draft.sold', e.pick.at, { overall: e.pick.overall, playerId: e.pick.playerId });
          break;
        case 'auto.set':
          ownBlocks.add(e.teamId);
          break;
        case 'pick.made':
          push('draft.picked', e.pick.at, { overall: e.pick.overall, playerId: e.pick.playerId });
          break;
        case 'turn.changed':
          // A pick, an undo and a resume each carry whose turn it is already.
          if (!moved) push('draft.turn', now);
          break;
        case 'paused':
          push('draft.paused', e.at);
          // Every player up has his clock stopped, and says so.
          if (auction) block = true;
          break;
        case 'resumed':
          push('draft.resumed', e.at);
          if (auction) block = true;
          break;
        case 'undone':
          push('draft.undone', now, { overall: e.overall, playerId: e.playerId });
          // The money came back with him.
          if (auction) block = true;
          break;
        case 'clock.set':
          push('draft.clock', now);
          break;
        case 'autopilot.changed':
          push('draft.seat', now, { teamId: e.teamId });
          break;
        case 'queue.updated':
          // ONE TEAM'S BUSINESS, SO NO PLACE IN THE SHARED SEQUENCE. A queue
          // goes only to its own team's sockets; numbered, it read as a gap on
          // every other client, and each of them asked for a resync. It carries
          // the last shared `seq` instead, is never kept in the ring, and a
          // reconnect's snapshot carries the queue as it stands.
          out.push({
            seq: this.seq,
            at: iso(now),
            type: 'draft.queue',
            teamId: e.teamId,
            own: true,
          });
          break;
        case 'completed':
          push('draft.completed', e.at);
          break;
        case 'traded':
          push('draft.traded', now, { overalls: this.tradeCells(e.trade), trade: e.trade });
          break;
        case 'trade.undone':
          push('draft.trade.undone', now, { overalls: this.tradeCells(e.trade), trade: e.trade });
          break;
        case 'offers.changed':
          // EACH TEAM'S OWN, like a queue: an offer is private until accepted.
          for (const teamId of e.teamIds) {
            out.push({
              seq: this.seq,
              at: iso(now),
              type: 'draft.offers',
              teamId,
              own: true,
              closed: e.closed
                .filter((c) => c.offer.fromTeamId === teamId || c.offer.toTeamId === teamId)
                .map((c) => ({
                  offerId: c.offer.id,
                  fromTeamId: c.offer.fromTeamId,
                  toTeamId: c.offer.toTeamId,
                  reason: c.reason,
                })),
            });
          }
          break;
        case 'rejected':
          break;
      }
    }
    if (block) push('draft.block', now);
    else
      for (const teamId of ownBlocks)
        out.push({ seq: this.seq, at: iso(now), type: 'draft.block', teamId, own: true });
    return out;
  }

  private refused(
    now: number,
    intentId: string,
    code: ErrorCode,
    message: string,
    reason?: string,
  ): RoomStep {
    return {
      changed: false,
      events: [],
      reply: {
        type: 'intent.rejected',
        seq: this.seq,
        at: iso(now),
        payload: { intentId, code, message, ...(reason ? { details: { reason } } : {}) },
      },
    };
  }

  /** The host's clock for the league's stopped hours, or none (`RoomClock`). */
  useClock(clock: RoomClock | null): void {
    this.clock = clock;
  }

  /** The engine's context: the league's, and the host's clock when it has one. */
  private engine(): DraftContext {
    return this.clock === null ? this.ctx : { ...this.ctx, ...this.clock };
  }

  /** The engine's context with who is in the room now (`DraftContext.isPresent`). */
  private withPresence(connected: ReadonlySet<string>): DraftContext {
    return { ...this.engine(), isPresent: (teamId) => connected.has(teamId) };
  }

  private run(
    intentId: string,
    intent: DraftIntent,
    now: number,
    connected: ReadonlySet<string>,
  ): RoomStep {
    const applied = apply(this.state, intent, now, this.withPresence(connected));
    const rejected = applied.events.find((e) => e.type === 'rejected');
    if (rejected && rejected.type === 'rejected') {
      return this.refused(now, intentId, WIDENED[rejected.code], rejected.message, rejected.code);
    }
    this.state = applied.state;
    return { changed: true, events: this.record(applied.events, now), reply: null };
  }

  /** One message from a socket. Permissions are settled here, before the engine is asked. */
  intent(
    viewer: Viewer,
    message: DraftClientMessage,
    now: number,
    connected: ReadonlySet<string> = new Set(),
  ): RoomStep {
    if (message.type === 'ping') {
      return {
        changed: false,
        events: [],
        reply: {
          type: 'pong',
          seq: this.seq,
          at: iso(now),
          payload: { sentAt: message.payload.sentAt },
        },
      };
    }
    if (message.type === 'resync') {
      return { changed: false, events: [], reply: null, resyncFrom: message.payload.since };
    }
    const { intentId } = message.payload;
    // A RETRY OF AN INTENT ALREADY ACTED ON is answered by what it did then,
    // which the client has or will catch up to; it is not done twice.
    if (this.seen.includes(intentId)) return { changed: false, events: [], reply: null };
    // THE DRAFT IS OVER ONCE IT IS COMPLETE: its rows are the league's, so
    // nothing here changes it — not an undo, and not a queue.
    if (this.state.status === 'complete') {
      return this.refused(now, intentId, 'conflict', 'The draft is over.', 'complete');
    }
    const step = this.act(viewer, message, now, connected);
    if (step.changed || step.startAsked) {
      this.seen = [...this.seen.slice(-199), intentId];
    }
    return step;
  }

  private act(
    viewer: Viewer,
    message: Exclude<DraftClientMessage, { type: 'ping' | 'resync' }>,
    now: number,
    connected: ReadonlySet<string>,
  ): RoomStep {
    const { intentId } = message.payload;
    const commissionerOnly = () =>
      this.refused(
        now,
        intentId,
        'forbidden',
        // A league mock has a host, not a commissioner (MOCK_DRAFT.md §6.2).
        this.prepared.mock === undefined
          ? 'Only the commissioner can do that.'
          : 'Only the host can do that.',
      );
    const seatless = () =>
      this.refused(now, intentId, 'forbidden', 'You do not have a seat in this draft.');

    switch (message.type) {
      case 'draft.start':
        if (!viewer.isCommissioner) return commissionerOnly();
        if (this.state.status !== 'lobby') {
          return this.refused(
            now,
            intentId,
            'conflict',
            'The draft has already started.',
            'already-started',
          );
        }
        return { changed: false, events: [], reply: null, startAsked: true };
      case 'draft.pick': {
        // ANOTHER SEAT'S PICK IS THE COMMISSIONER'S TO MAKE, and only for the
        // team on the clock, which the engine checks.
        const teamId = message.payload.teamId ?? viewer.teamId;
        if (teamId === null) return seatless();
        if (teamId !== viewer.teamId && !viewer.isCommissioner) return commissionerOnly();
        return this.run(
          intentId,
          { type: 'pick', teamId, playerId: message.payload.playerId, madeBy: viewer.userId },
          now,
          connected,
        );
      }
      case 'draft.queue.set':
        if (viewer.teamId === null) return seatless();
        return this.run(
          intentId,
          { type: 'queue.set', teamId: viewer.teamId, playerIds: message.payload.playerIds },
          now,
          connected,
        );
      case 'draft.pause':
        if (!viewer.isCommissioner) return commissionerOnly();
        return this.run(intentId, { type: 'pause', by: viewer.name }, now, connected);
      case 'draft.resume':
        if (!viewer.isCommissioner) return commissionerOnly();
        return this.run(intentId, { type: 'resume', by: viewer.name }, now, connected);
      case 'draft.undo':
        if (!viewer.isCommissioner) return commissionerOnly();
        return this.run(intentId, { type: 'undo', by: viewer.name }, now, connected);
      case 'draft.setClock':
        if (!viewer.isCommissioner) return commissionerOnly();
        return this.run(
          intentId,
          { type: 'clock.set', by: viewer.name, seconds: message.payload.seconds },
          now,
          connected,
        );
      case 'draft.trade.offer': {
        if (viewer.teamId === null) return seatless();
        const { toTeamId, give, get, replaces } = message.payload;
        const refusal = this.tradeRefusal(now, intentId, viewer.teamId, toTeamId);
        if (refusal !== null) return refusal;
        return this.run(
          intentId,
          {
            type: 'trade.offer',
            // The intent's own id, which is new each time and the same on a retry.
            id: intentId,
            fromTeamId: viewer.teamId,
            toTeamId,
            give,
            get,
            ...(replaces === undefined ? {} : { replaces }),
          },
          now,
          connected,
        );
      }
      case 'draft.trade.answer':
        if (viewer.teamId === null) return seatless();
        return this.run(
          intentId,
          {
            type: 'trade.answer',
            teamId: viewer.teamId,
            offerId: message.payload.offerId,
            accept: message.payload.accept,
          },
          now,
          connected,
        );
      case 'draft.trade.withdraw':
        if (viewer.teamId === null) return seatless();
        return this.run(
          intentId,
          { type: 'trade.withdraw', teamId: viewer.teamId, offerId: message.payload.offerId },
          now,
          connected,
        );
      case 'draft.trade.undo':
        if (!viewer.isCommissioner) return commissionerOnly();
        return this.run(intentId, { type: 'trade.undo', by: viewer.name }, now, connected);
      case 'draft.seat.take':
        return this.seatChange(viewer, intentId, message.payload.teamId, now);
      case 'draft.seat.leave':
        return this.seatChange(viewer, intentId, null, now);
      case 'draft.nominate': {
        // THE TEAM NOMINATING'S, or the commissioner's for it, as a pick is.
        const teamId = message.payload.teamId ?? viewer.teamId;
        if (teamId === null) return seatless();
        if (teamId !== viewer.teamId && !viewer.isCommissioner) return commissionerOnly();
        return this.run(
          intentId,
          {
            type: 'nominate',
            teamId,
            playerId: message.payload.playerId,
            bid: message.payload.bid,
            madeBy: viewer.userId,
          },
          now,
          connected,
        );
      }
      case 'draft.bid':
        // NOBODY BIDS FOR ANOTHER TEAM, the commissioner included (AUCTION.md §3.8).
        if (viewer.teamId === null) return seatless();
        return this.run(
          intentId,
          {
            type: 'bid',
            teamId: viewer.teamId,
            lotId: message.payload.lotId,
            amount: message.payload.amount,
            madeBy: viewer.userId,
          },
          now,
          connected,
        );
      case 'draft.bid.auto':
        if (viewer.teamId === null) return seatless();
        return this.run(
          intentId,
          {
            type: 'bid.auto',
            teamId: viewer.teamId,
            lotId: message.payload.lotId,
            max: message.payload.max,
            madeBy: viewer.userId,
          },
          now,
          connected,
        );
      case 'draft.autopilot.set': {
        const teamId = message.payload.teamId ?? viewer.teamId;
        if (teamId === null) return seatless();
        // ANOTHER SEAT'S AUTO IS THE COMMISSIONER'S, which is the way past a
        // manager who has gone quiet under the soft timer.
        if (teamId !== viewer.teamId && !viewer.isCommissioner) return commissionerOnly();
        return this.run(
          intentId,
          { type: 'autopilot.set', teamId, on: message.payload.on },
          now,
          connected,
        );
      }
    }
  }

  /**
   * Why this offer is refused before the engine is asked, or null: a mock, a
   * league that does not trade during its draft, and a seat no person holds
   * are the room's to know, not the engine's.
   */
  private tradeRefusal(
    now: number,
    intentId: string,
    fromTeamId: string,
    toTeamId: string,
  ): RoomStep | null {
    if (this.prepared.mock !== undefined) {
      return this.refused(
        now,
        intentId,
        'forbidden',
        'A mock has no trades: it is practice against the board.',
      );
    }
    if (this.state.trading !== true) {
      return this.refused(
        now,
        intentId,
        'forbidden',
        'This league does not trade during its draft.',
        'bad-trade',
      );
    }
    const seat = this.prepared.seats.find((s) => s.teamId === toTeamId);
    if (seat === undefined) {
      return this.refused(
        now,
        intentId,
        'invalid',
        'That team is not in this draft.',
        'unknown-team',
      );
    }
    if (toTeamId === fromTeamId) {
      return this.refused(now, intentId, 'invalid', 'A trade takes two teams.', 'bad-trade');
    }
    // A PERSON ANSWERS AN OFFER. A bot's trades are the AI managers' work,
    // not the room's, and a seat nobody holds has nobody to ask.
    if (seat.bot !== null || seat.ownerUserId === null) {
      return this.refused(
        now,
        intentId,
        'conflict',
        'Only a team a person runs can take a trade. Bots do not trade in the draft room.',
        'bad-trade',
      );
    }
    return null;
  }

  /** The seat a person took in a league mock's lobby, or null (MOCK_DRAFT.md §6.2). */
  seatOf(userId: string): string | null {
    return this.prepared.seats.find((s) => s.ownerUserId === userId)?.teamId ?? null;
  }

  /** A league mock's seat, taken or given up: nobody else's, and only in the lobby. */
  private seatChange(
    viewer: Viewer,
    intentId: string,
    teamId: string | null,
    now: number,
  ): RoomStep {
    if (this.prepared.mock === undefined) {
      return this.refused(now, intentId, 'forbidden', 'The seats here are the league’s teams’.');
    }
    if (this.state.status !== 'lobby') {
      return this.refused(
        now,
        intentId,
        'conflict',
        'The mock has started; the seats are set.',
        'already-started',
      );
    }
    const wanted =
      teamId === null ? null : (this.prepared.seats.find((s) => s.teamId === teamId) ?? null);
    if (teamId !== null && wanted === null) {
      return this.refused(
        now,
        intentId,
        'invalid',
        'That seat is not in this mock.',
        'unknown-team',
      );
    }
    if (wanted !== null && wanted.ownerUserId !== null && wanted.ownerUserId !== viewer.userId) {
      return this.refused(now, intentId, 'conflict', `${wanted.team.ownerName} is in that seat.`);
    }
    // ONE SEAT A PERSON: taking one gives up any other. And WHAT THEY SET GOES
    // WITH THEM: their queue and their auto move to the seat they take, and a
    // seat they leave starts clean for whoever sits in it next.
    const from = this.prepared.seats.find((s) => s.ownerUserId === viewer.userId)?.teamId ?? null;
    if (from !== null && from !== wanted?.teamId) {
      const queues = { ...this.state.queues };
      const autopilot = { ...this.state.autopilot };
      const queue = queues[from];
      const auto = autopilot[from];
      delete queues[from];
      delete autopilot[from];
      if (wanted !== null) {
        if (queue !== undefined) queues[wanted.teamId] = queue;
        if (auto) autopilot[wanted.teamId] = true;
      }
      this.state = { ...this.state, queues, autopilot };
    }
    this.prepared = {
      ...this.prepared,
      seats: this.prepared.seats.map((s) => {
        if (wanted !== null && s.teamId === wanted.teamId) {
          return { ...s, ownerUserId: viewer.userId, team: { ...s.team, ownerName: viewer.name } };
        }
        if (s.ownerUserId === viewer.userId) {
          return { ...s, ownerUserId: null, team: { ...s.team, ownerName: OPEN_SEAT } };
        }
        return s;
      }),
    };
    return { changed: true, events: [], reply: null, seatsChanged: true };
  }

  /** Somebody from a seat arrived or the last of them left: the seat's `connected` changed. */
  presence(teamId: string, now: number): RingEvent {
    this.seq += 1;
    return { seq: this.seq, at: iso(now), type: 'draft.seat', teamId };
  }

  /** The draft begins: the host has seated the bots and handed back a fresh read. */
  start(now: number, by: string, connected: ReadonlySet<string> = new Set()): RoomStep {
    const applied = apply(this.state, { type: 'start', by }, now, this.withPresence(connected));
    if (applied.events.some((e) => e.type === 'rejected')) {
      return { changed: false, events: [], reply: null };
    }
    this.state = applied.state;
    return { changed: true, events: this.record(applied.events, now), reply: null };
  }

  /** The room by itself: bots, seats on auto and clocks that ran out, up to `now`. */
  tick(now: number, connected: ReadonlySet<string> = new Set()): RoomStep {
    const advanced = advance(this.state, now, this.withPresence(connected));
    if (advanced.events.length === 0) return { changed: false, events: [], reply: null };
    this.state = advanced.state;
    return { changed: true, events: this.record(advanced.events, now), reply: null };
  }

  /**
   * When the room next has something to do by itself. `connected` is who is
   * in the room: an auction bids for a seat whose manager is not
   * (AUCTION.md §6.1), so the alarm has to know whose bids are coming.
   */
  nextAlarm(connected?: ReadonlySet<string>): number | null {
    if (this.state.status === 'lobby') {
      const at = this.prepared.scheduledAt;
      return this.prepared.mode === 'live' && at !== null ? Date.parse(at) : null;
    }
    return nextAt(
      this.state,
      connected === undefined ? this.engine() : this.withPresence(connected),
    );
  }
}
